/** Shared act / scene / song marker label rules for toolbar, sidebar, and on-page edit. */

/**
 * Seed text for a new marker's label.
 *
 * ⚠ SONG IS DELIBERATELY EMPTY. It used to be 'Song ', and that word was then
 * stored inside the label, locked so it could not be deleted, and stripped back
 * out again for the sidebar — three pieces of code disagreeing about whether it
 * should exist. A song's type is now shown as the note glyph the app already
 * draws (components/project/bookmark/bookmark-type-icon.vue), DERIVED from
 * `marker.type` rather than baked into the text.
 *
 * That is the actual fix rather than a tidy-up: the type lived in two places, so
 * the two creation paths could disagree about it. A song created by placing a
 * mark on the page stored "Song Defying Gravity"; one created through Manage
 * Structure stored "Defying Gravity". Both looked identical in the sidebar,
 * because the strip hid the difference, and different on the page. With the
 * glyph derived from the type, both paths store only the title and cannot
 * diverge.
 *
 * Act and Scene keep their words: what follows them is a NUMBER, so "Act 1"
 * reads correctly, while "Song Defying Gravity" is not how anyone says it.
 */
export const MARKER_TYPE_PREFIX = {
    act: 'Act ',
    scene: 'Scene ',
    song: ''
};

export function defaultMarkerLabel(type) {
    const prefix = MARKER_TYPE_PREFIX[type];
    // ⚠ Explicit undefined check, NOT `||`. Song's prefix is an empty string,
    // which is falsy — a `||` fallback here would hand every song "Scene ".
    return prefix === undefined ? MARKER_TYPE_PREFIX.scene : prefix;
}

/**
 * The label as it should be READ, anywhere it is shown.
 *
 * ⚠ This is now a LEGACY SHIM, not a design feature. Markers created before the
 * note glyph still carry a stored "Song " in their label. Stripping it here
 * means those render identically to new ones — on the page and in the sidebar —
 * so nothing has to be rewritten in Firestore. Deleting this function would
 * make every pre-existing song mark read "♪ Song Defying Gravity".
 *
 * It can go once no stored label starts with "Song ", which may be never, and
 * costs nothing to keep.
 */
export function markerDisplayLabel(marker) {
    const label = (marker && marker.label) ? String(marker.label) : '';
    if (marker && marker.type === 'song') {
        const lower = label.toLowerCase();
        if (lower.startsWith('song ')) {
            const rest = label.slice(5).trim();
            return rest || label;
        }
    }
    return label;
}

/**
 * Sidebar and list-view row text after the page number; songs take single
 * quotes, which is what marks a title as a title once the word is gone.
 *
 * ⚠ Returns TEXT ONLY, deliberately — no glyph. This also feeds the CSV export
 * (`list-view.vue` csvSectionRow), and a note character has no business in a
 * data file. The glyph is a rendered component, never part of a string.
 */
export function sidebarMarkerRowText(marker) {
    const label = markerDisplayLabel(marker);
    if (!label) {
        return '';
    }
    if (marker && marker.type === 'song') {
        const trimmed = label.trim();
        if (trimmed.startsWith("'") || trimmed.startsWith('"')) {
            return trimmed;
        }
        return `'${trimmed}'`;
    }
    return label;
}

export function markerTypeFromToolIndex(index) {
    const types = ['act', 'scene', 'song'];
    const i = Number(index);
    return types[i] || 'scene';
}

export function markerToolIndexFromType(type) {
    if (type === 'act') return 0;
    if (type === 'song') return 2;
    return 1;
}


// WEBPACK FOOTER //
// ./src/lib/marker-labels.js